import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Landmark, Clock, CheckCircle2, ArrowRight, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function PayoutsPanel({ copy }) {
  const [status, setStatus] = useState(null);
  const [payouts, setPayouts] = useState({ items: [], pending_total: 0, paid_total: 0 });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [s, p] = await Promise.all([
      api.get("/connect/status").catch(() => ({ data: { connected: false } })),
      api.get("/my/payouts"),
    ]);
    setStatus(s.data);
    setPayouts(p.data);
  };
  useEffect(() => { load(); }, []);

  // Re-check after Stripe onboarding redirect
  useEffect(() => {
    const u = new URL(window.location.href);
    if (u.searchParams.get("connect") === "done" || u.searchParams.get("connect") === "refresh") {
      load();
      u.searchParams.delete("connect");
      window.history.replaceState({}, "", u.pathname + (u.search ? u.search : ""));
      toast.success("Welcome back! Refreshing your Stripe status…");
    }
  }, []);

  const onboard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/connect/onboard");
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open Stripe onboarding");
      setBusy(false);
    }
  };

  const payoutsEnabled = status?.payouts_enabled;
  const details = status?.details_submitted;

  return (
    <div className="p-6 rounded-2xl bg-[#131722] border border-slate-800" data-testid="payouts-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl font-bold flex items-center gap-2"><Landmark className="w-5 h-5 text-emerald-400" /> Payouts (Stripe Connect)</h2>
          <p className="text-xs text-slate-400 mt-1">{copy || "80% to you · 10% platform · released the day after each trip starts."}</p>
        </div>
        <Button size="sm" onClick={onboard} disabled={busy} data-testid="connect-stripe-btn"
          className={payoutsEnabled ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30"
                    : "bg-emerald-500 text-slate-950 hover:bg-emerald-400 font-semibold"}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" />
            : payoutsEnabled ? <><CheckCircle2 className="w-4 h-4 mr-1.5" />Payouts active · Manage</>
            : details ? <>Finish onboarding <ArrowRight className="w-4 h-4 ml-1.5" /></>
            : <>Connect with Stripe <ArrowRight className="w-4 h-4 ml-1.5" /></>}
        </Button>
      </div>

      {status?.connected && !payoutsEnabled && (
        <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm flex items-start gap-2" data-testid="connect-pending">
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-amber-200 font-medium">Stripe onboarding incomplete</p>
            <p className="text-xs text-amber-300/80 mt-0.5">
              {status.disabled_reason ? `Reason: ${status.disabled_reason}. ` : ""}Finish verification to start receiving payouts.
            </p>
          </div>
        </div>
      )}
      {payoutsEnabled && (
        <div className="mb-4 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm" data-testid="connect-active">
          <span className="text-emerald-300">Stripe account:</span> <span className="font-mono text-xs">{status.stripe_account_id}</span> · <span className="text-emerald-400">payouts enabled</span>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="p-4 rounded-xl bg-slate-900 border border-slate-800">
          <p className="text-xs font-mono uppercase tracking-widest text-amber-400 flex items-center gap-1.5"><Clock className="w-3 h-3" />Pending</p>
          <p className="font-display font-extrabold text-2xl mt-1" data-testid="pending-total">${payouts.pending_total.toFixed(2)}</p>
        </div>
        <div className="p-4 rounded-xl bg-slate-900 border border-slate-800">
          <p className="text-xs font-mono uppercase tracking-widest text-emerald-400">Paid</p>
          <p className="font-display font-extrabold text-2xl mt-1" data-testid="paid-total">${payouts.paid_total.toFixed(2)}</p>
        </div>
      </div>

      {payouts.items.length === 0 ? (
        <p className="text-sm text-slate-500">No payouts yet.</p>
      ) : (
        <div className="space-y-2">
          {payouts.items.slice(0, 8).map((p) => (
            <div key={p.payout_id} className="flex items-center justify-between p-3 rounded-lg bg-slate-900/60 border border-slate-800" data-testid={`payout-${p.payout_id}`}>
              <div>
                <p className="text-sm font-mono">{p.payout_id}</p>
                <p className="text-xs text-slate-500">Release {new Date(p.release_at).toLocaleDateString()}</p>
              </div>
              <div className="text-right">
                <p className="font-mono text-emerald-400 font-semibold">${p.amount.toFixed(2)}</p>
                <Badge className={`text-[10px] ${p.status === "paid" ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" : "bg-amber-500/20 text-amber-300 border-amber-500/30"}`}>{p.status}</Badge>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
